import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, SearchField, Tooltip } from '@heroui/react';
import { useDataStore } from '@/stores/dataStore';
import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { railUnreadLabel } from './folderRules';

/**
 * Overflowing servers collapse into an overlapping avatar stack (HeroUI
 * "Avatar Group" look) ending with a "+N" tile. Tapping it opens a searchable
 * popover with the full server list. The overlap is the compact indicator;
 * actual selection happens in the popover (overlapped tiles aren't reliable
 * individual tap targets). Extracted from HeroTopBar.
 */
export function ServerOverflowStack(props: {
  overflowIds: string[];
  guildOrder: string[];
  guilds: ReturnType<typeof useDataStore.getState>['guilds'];
  currentGuildId: string | null;
  /** Unread total per guild (railSelectors.selectUnreadByGuild). */
  unreadByGuild: Record<string, number>;
  onSelect: (id: string) => void;
  onReorder: (draggedId: string, targetId: string, pos: 'before' | 'after') => void;
  /** How many avatars may peek out behind the "+N" tile. The caller lowers it
   *  (down to 0) when the header is narrow — each preview adds ~35 px, and a
   *  phone doesn't have them to give: the stack used to keep its full width and
   *  spill over the Beta/theme/language buttons. */
  previewCount?: number;
  /** Render one size down. Set when the header is too tight for the full tile;
   *  a smaller chip beats one sliced in half by the rail's clip guard. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return props.guildOrder
      .map((id) => props.guilds[id])
      .filter((g): g is NonNullable<typeof g> => !!g)
      .filter((g) => !q || g.name.toLowerCase().includes(q));
  }, [props.guildOrder, props.guilds, query]);

  // Up to `previewCount` overflow avatars peek out behind the "+N" tile.
  const preview = props.overflowIds.slice(0, Math.max(0, props.previewCount ?? 3));
  const remaining = props.overflowIds.length;

  // Drag-over state within the popover list.
  const draggingIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragOverPos, setDragOverPos] = useState<'before' | 'after'>('before');

  function rowDragProps(id: string) {
    return {
      draggable: true as const,
      onDragStart: (e: React.DragEvent) => {
        e.dataTransfer.setData('text/plain', id);
        e.dataTransfer.effectAllowed = 'move';
        draggingIdRef.current = id;
        setDragOverId(null);
      },
      onDragOver: (e: React.DragEvent) => {
        const current = draggingIdRef.current;
        if (!current || current === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
        setDragOverId(id);
        setDragOverPos(pos);
      },
      onDragLeave: () => setDragOverId((prev) => (prev === id ? null : prev)),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const current = draggingIdRef.current;
        if (!current || current === id) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
        props.onReorder(current, id, pos);
        draggingIdRef.current = null;
        setDragOverId(null);
      },
      onDragEnd: () => {
        draggingIdRef.current = null;
        setDragOverId(null);
      },
    };
  }

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Tooltip delay={0}>
        {/* Popover.Trigger is itself the tooltip's trigger (no second
            focusable wrapper — one Tab stop, one label). */}
        <Popover.Trigger aria-label={t('ui.topbar.moreServers', { count: remaining })} className="studio-server-overflow flex shrink-0 items-center rounded-xl">
            {preview.map((id, i) => {
              const g = props.guilds[id];
              if (!g) return null;
              return (
                // Explicit negative margin (Tailwind's -space-x utility wasn't
                // emitted here) makes the avatars overlap; later siblings paint
                // on top, so the "+N" tile sits frontmost.
                <span key={id} style={i === 0 ? undefined : { marginInlineStart: '-13.6px' }}>
                  <IdentityAvatar id={g.id} name={g.name} src={g.iconUrl} size="md" className={props.compact ? '!size-[40px]' : '!size-[48px]'} />
                </span>
              );
            })}
            <span className="studio-avatar font-semibold" style={{ width: props.compact ? 44 : 48, height: props.compact ? 44 : 48, marginInlineStart: preview.length > 0 ? '-13.6px' : undefined }} aria-hidden="true">+{remaining}</span>
          </Popover.Trigger>
        <Tooltip.Content>{t('ui.topbar.allServers')}</Tooltip.Content>
      </Tooltip>
      <Popover.Content className="studio-server-picker w-72 max-w-[calc(100vw-1.5rem)]" placement="bottom end">
        <Popover.Dialog className="p-2">
          <SearchField
            aria-label={t('ui.topbar.searchServer')}
            variant="secondary"
            value={query}
            onChange={setQuery}
            className="mb-2"
          >
            <SearchField.Group className="rounded-[6px]">
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={t('ui.topbar.searchServerPlaceholder')} />
              <SearchField.ClearButton aria-label={t('ui.topbar.clear')} />
            </SearchField.Group>
          </SearchField>
          <div className="scrollbar-thin flex max-h-[50vh] flex-col gap-0.5 overflow-y-auto">
            {rows.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted">{t('ui.topbar.nothingFound')}</p>
            ) : (
              rows.map((g) => {
                const unread = props.unreadByGuild[g.id] ?? 0;
                const active = g.id === props.currentGuildId;
                const isOver = dragOverId === g.id;
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => {
                      props.onSelect(g.id);
                      setOpen(false);
                    }}
                    {...rowDragProps(g.id)}
                    className={[
                      'flex cursor-grab items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors',
                      active ? 'bg-primary/15 text-foreground' : 'hover:bg-default/50',
                      isOver && dragOverPos === 'before' ? 'border-t-2 border-primary' : '',
                      isOver && dragOverPos === 'after' ? 'border-b-2 border-primary' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <IdentityAvatar id={g.id} name={g.name} src={g.iconUrl} />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground/90">
                      {g.name}
                    </span>
                    {railUnreadLabel(unread) && (
                      <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-bg tabular-nums">
                        {railUnreadLabel(unread)}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
